import { Box, Divider, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import FormulaCard from '../../../components/search/FormulaCard';
import { FormulaService } from '../../../components/search/formulaService';
import { toast } from '../../../helpers/toast';
import { Formula, FormulaSummary } from '../../../interfaces/formula';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { PrescriptionService } from '../service';
import FormulaFormDialog from './components/FormulaFormDialog';
import { amplitudeModule } from '../../../shared/amplitude';

interface PrescriptionFormulaContentProps {
  prescriptionId: number;
  doctorId: number;
  isFormulaFormDialogOpen: boolean;
  setIsFormulaFormDialogOpen: (isOpen: boolean) => void;
  selectedFormula: Formula | null;
  setSelectedFormula: (formula: Formula | null) => void;
}

export default function PrescriptionFormulaContent({
  prescriptionId,
  doctorId,
  isFormulaFormDialogOpen,
  setIsFormulaFormDialogOpen,
  selectedFormula,
  setSelectedFormula,
}: PrescriptionFormulaContentProps) {
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [isEditingFormula, setIsEditingFormula] = useState(false);
  const [page] = useState(1);
  const [pageSize] = useState(10);
  const setPrescription = usePrescriptionStore((store) => store.setPrescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const prescription = usePrescriptionStore((store) => store.prescription);
  const formulasManipuladas = usePrescriptionStore((store) => store.prescription?.Formulas);
  const previousFormulasRef = useRef<Formula[] | undefined>(undefined);

  const handleDeleteFormula = async (formulaId: number) => {
    const formulaSummary = (formulasManipuladas as unknown as FormulaSummary[] | undefined)?.find((f) => f.id === formulaId);
    amplitudeModule.removePrescriptionItemClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      compounded_medication: [{
        id: formulaId,
        name: formulaSummary?.NomeFormula ?? '',
      }],
    });

    amplitudeModule.compoundedMedicationRemoved({
      validation_code: prescription?.CodigoValidacao ?? '',
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      compounded_medication_id: formulaId,
      name: formulaSummary?.NomeFormula ?? '',
    });

    await FormulaService.deleteFormulaFromPrescription(prescriptionId, formulaId);

    // Atualiza a prescrição no store para refletir as mudanças nos itens prescritivos
    const updatedPrescription = await PrescriptionService.fetchPrescriptionById(prescriptionId);
    setPrescription(updatedPrescription);

    toast.success('Fórmula removida com sucesso');
    setIsFormulaFormDialogOpen(false);
    refreshFormulas();
  };

  const refreshFormulas = useCallback(async () => {
    const data = await FormulaService.fetchFormulas(prescriptionId, page, pageSize);
    setFormulas(data.items);
  }, [prescriptionId, page, pageSize]);

  const handleCloseFormulaFormDialog = () => {
    setIsFormulaFormDialogOpen(false);
    setSelectedFormula(null);
    setIsEditingFormula(false);
  };

  const handleEditFormula = (formula: Formula) => {
    const summary = (formulasManipuladas as unknown as FormulaSummary[] | undefined)?.find((f) => f.id === formula.id);
    amplitudeModule.editPrescriptionItemClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      compounded_medication: [{
        id: formula.id,
        name: summary?.NomeFormula ?? formula.formulaName ?? '',
      }],
    });

    setIsEditingFormula(true);
    setSelectedFormula(formula);
    setIsFormulaFormDialogOpen(true);
  };

  const handleSetIsEditingFormula = (isEditing: boolean) => {
    setIsEditingFormula(isEditing);
  };

  useEffect(() => {
    refreshFormulas();
  }, [prescriptionId, page, pageSize, refreshFormulas]);

  // Reseta isEditingFormula e garante que selectedFormula seja null quando o modal é aberto para criar uma nova fórmula
  useEffect(() => {
    if (isFormulaFormDialogOpen && !selectedFormula) {
      setIsEditingFormula(false);
      // Garante que selectedFormula seja null ao criar nova fórmula
      setSelectedFormula(null);
    }
  }, [isFormulaFormDialogOpen, selectedFormula, setSelectedFormula]);

  // Atualiza a lista de fórmulas quando a prescrição for atualizada (ex: após aplicar um modelo)
  useEffect(() => {
    // Compara a referência do array para detectar mudanças
    const hasChanged = previousFormulasRef.current !== formulasManipuladas;

    if (hasChanged) {
      refreshFormulas();
      previousFormulasRef.current = formulasManipuladas;
    }
  }, [formulasManipuladas, refreshFormulas]);

  return (
    <div>
      {formulas && formulas.length > 0 && (
        <Box mt={2}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Text fontSize={'16px'} fontWeight={500} color={'typography.main'} whiteSpace={'nowrap'}>
              Fórmulas
            </Text>
            <Divider m={'0 8px'} />
          </div>
        </Box>
      )}
      {formulas &&
        formulas.length > 0 &&
        formulas.map((formula: Formula, index: number) => (
          <FormulaCard
            key={formula.id}
            formula={formula}
            index={index}
            doctorId={doctorId}
            onEditFormula={handleEditFormula}
            handleDeleteFormula={handleDeleteFormula}
          />
        ))}
      {/* {formulas.length === 0 && <PrescriptionModels />} */}
      {isFormulaFormDialogOpen && (
        <FormulaFormDialog
          onClose={handleCloseFormulaFormDialog}
          selectedFormula={selectedFormula}
          isEditingFormula={isEditingFormula}
          refreshFormulas={refreshFormulas}
          handleSetIsEditingFormula={handleSetIsEditingFormula}
        />
      )}
    </div>
  );
}
